import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Button,
} from '@mui/material';
import { ExportFieldOption } from '../../utils/exportApi';

interface CsvFieldSelectorProps {
  fields: ExportFieldOption[];
  selectedFields: Set<string>;
  onToggleField: (fieldName: string) => void;
  onSelectAllInCategory: (category: string) => void;
  onDeselectAllInCategory: (category: string) => void;
}

const CATEGORY_LABELS: Record<string, string> = {
  core: 'Core',
  description: 'Description',
  measurements: 'Measurements',
  cataloging: 'Cataloging',
  provenance: 'Provenance',
  exhibitions: 'Exhibitions',
  additional: 'Additional Info',
};

export const CsvFieldSelector = ({
  fields,
  selectedFields,
  onToggleField,
  onSelectAllInCategory,
  onDeselectAllInCategory,
}: CsvFieldSelectorProps) => {
  const categories = [...new Set(fields.map((f) => f.category))];

  const getFieldsByCategory = (category: string) =>
    fields.filter((f) => f.category === category);

  const allSelectedInCategory = (category: string) =>
    getFieldsByCategory(category).every((f) => selectedFields.has(f.name));

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {selectedFields.size} of {fields.length} fields selected
      </Typography>
      {categories.map((category) => (
        <Box key={category} sx={{ mb: 2 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 0.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {CATEGORY_LABELS[category] || category}
            </Typography>
            <Button
              size="small"
              onClick={() =>
                allSelectedInCategory(category)
                  ? onDeselectAllInCategory(category)
                  : onSelectAllInCategory(category)
              }
            >
              {allSelectedInCategory(category) ? 'Deselect All' : 'Select All'}
            </Button>
          </Box>
          <FormGroup>
            {getFieldsByCategory(category).map((field) => (
              <FormControlLabel
                key={field.name}
                control={
                  <Checkbox
                    checked={selectedFields.has(field.name)}
                    onChange={() => onToggleField(field.name)}
                    size="small"
                  />
                }
                label={field.label}
                sx={{ ml: 0 }}
              />
            ))}
          </FormGroup>
        </Box>
      ))}
    </Box>
  );
};
